import React, { useRef, useEffect, useState, useCallback } from "react";
import { motion, useAnimation, PanInfo } from "framer-motion";
import styled from "styled-components";
import { IoIosSearch } from "react-icons/io";
import { IoMdPerson, IoMdMusicalNote } from "react-icons/io";
import { IoCalendar, IoChevronUp } from "react-icons/io5";

// Snap points for the bottom sheet (in viewport percentage)
const SNAP_POINTS = {
    MIN: 12,       // Collapsed state (just showing header)
    MID: 55,       // 55% of viewport
    MAX: 92,       // Almost full screen
};

const Overlay = styled(motion.div)`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 9;
    pointer-events: none;
`;

const BottomSheetContainer = styled(motion.div)`
    display: flex;
    flex-direction: column;
    position: fixed;
    z-index: 10;
    left: 0;
    right: 0;
    bottom: 0;
    border-top-left-radius: 20px;
    border-top-right-radius: 20px;
    box-shadow: 0px -4px 24px rgba(0, 0, 0, 0.12);
    background: var(--bg-color);
    touch-action: none;
    will-change: transform;
    border-top: 1px solid var(--gray-color);
`;

const HandleArea = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 8px 16px 12px;
    cursor: grab;
    user-select: none;
    background: var(--bg-color);
    border-top-left-radius: 20px;
    border-top-right-radius: 20px;

    &:active {
        cursor: grabbing;
    }
`;

const HandleBar = styled.div`
    width: 36px;
    height: 4px;
    background: var(--gray-color);
    border-radius: 2px;
    margin-bottom: 12px;
`;

const SearchContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 8px 12px;
    background: var(--gray-color);
    border-radius: 12px;
    margin-bottom: 8px;
`;

const SearchTypeButton = styled.button`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 6px;
    border: none;
    background: transparent;
    color: var(--text-color);
    cursor: pointer;
    border-radius: 6px;
    transition: background 0.2s;

    &:hover {
        background: rgba(255, 255, 255, 0.1);
    }
`;

const SearchInput = styled.input`
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
    color: var(--text-color);
    font-size: 0.85rem;
    padding: 4px 0;

    &::placeholder {
        color: var(--text-color);
        opacity: 0.5;
    }
`;

const IconButton = styled.button`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 6px;
    border: none;
    background: transparent;
    color: var(--text-color);
    cursor: pointer;
    border-radius: 6px;
    transition: all 0.2s;

    &:hover {
        background: rgba(255, 255, 255, 0.1);
    }

    &:active {
        transform: scale(0.95);
    }
`;

const SheetHeader = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 0 4px;
`;

const HeaderInfo = styled.span`
    font-size: 0.7rem;
    color: var(--text-color);
    opacity: 0.6;
`;

const ExpandButton = styled.button`
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    border: none;
    background: transparent;
    color: var(--text-color);
    opacity: 0.6;
    font-size: 0.7rem;
    cursor: pointer;
    border-radius: 4px;
    transition: all 0.2s;

    &:hover {
        opacity: 1;
        background: var(--gray-color);
    }

    svg {
        transition: transform 0.3s;
    }

    &.expanded svg {
        transform: rotate(180deg);
    }
`;

const ContentArea = styled.div`
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
    background: var(--bg-color);

    &::-webkit-scrollbar {
        width: 4px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background: var(--gray-color);
        border-radius: 2px;
    }
`;

interface BottomSheetProps {
    children: React.ReactNode;
    songCount?: number;
    totalCount?: number;
    searchValue: string;
    searchType: string;
    onSearchValueChange: (value: string) => void;
    onSearchTypeToggle: () => void;
    onSearch: () => void;
    onCalendarToggle: () => void;
    shouldCollapse?: boolean;
    onCollapseComplete?: () => void;
}

const CustomBottomSheet = ({
    children,
    songCount = 0,
    totalCount = 0,
    searchValue,
    searchType,
    onSearchValueChange,
    onSearchTypeToggle,
    onSearch,
    onCalendarToggle,
    shouldCollapse = false,
    onCollapseComplete,
}: BottomSheetProps) => {
    const controls = useAnimation();
    const containerRef = useRef<HTMLDivElement>(null);
    const [sheetHeight, setSheetHeight] = useState(SNAP_POINTS.MID);

    // Collapse when shouldCollapse becomes true
    useEffect(() => {
        if (shouldCollapse) {
            setSheetHeight(SNAP_POINTS.MIN);
            controls.start({
                height: (window.innerHeight * SNAP_POINTS.MIN) / 100,
                transition: {
                    type: "spring",
                    damping: 25,
                    stiffness: 300,
                },
            }).then(() => {
                onCollapseComplete?.();
            });
        }
    }, [shouldCollapse, controls, onCollapseComplete]);

    // Calculate height in pixels based on viewport percentage
    const getHeightPx = useCallback((percentage: number) => {
        return (window.innerHeight * percentage) / 100;
    }, []);

    // Initialize to MID position
    useEffect(() => {
        const height = getHeightPx(SNAP_POINTS.MID);
        setSheetHeight(SNAP_POINTS.MID);
        controls.set({ height });
    }, [controls, getHeightPx]);

    // Handle drag end - snap to nearest point
    const handleDragEnd = useCallback(async (
        _event: MouseEvent | TouchEvent | PointerEvent,
        info: PanInfo
    ) => {
        const velocity = info.velocity.y;
        const currentHeight = containerRef.current?.offsetHeight || getHeightPx(SNAP_POINTS.MID);
        const viewportHeight = window.innerHeight;
        const currentPercentage = (currentHeight / viewportHeight) * 100;

        let targetSnap = SNAP_POINTS.MID;

        // Determine target based on velocity and position
        if (Math.abs(velocity) > 500) {
            // Fast swipe
            if (velocity < 0) {
                // Swiping up (expanding)
                if (currentPercentage < SNAP_POINTS.MID) {
                    targetSnap = SNAP_POINTS.MID;
                } else {
                    targetSnap = SNAP_POINTS.MAX;
                }
            } else {
                // Swiping down (collapsing)
                if (currentPercentage > SNAP_POINTS.MID) {
                    targetSnap = SNAP_POINTS.MID;
                } else {
                    targetSnap = SNAP_POINTS.MIN;
                }
            }
        } else {
            // Slow drag - snap to nearest
            const distToMin = Math.abs(currentPercentage - SNAP_POINTS.MIN);
            const distToMid = Math.abs(currentPercentage - SNAP_POINTS.MID);
            const distToMax = Math.abs(currentPercentage - SNAP_POINTS.MAX);

            if (distToMin <= distToMid && distToMin <= distToMax) {
                targetSnap = SNAP_POINTS.MIN;
            } else if (distToMid <= distToMax) {
                targetSnap = SNAP_POINTS.MID;
            } else {
                targetSnap = SNAP_POINTS.MAX;
            }
        }

        setSheetHeight(targetSnap);
        await controls.start({
            height: getHeightPx(targetSnap),
            transition: {
                type: "spring",
                damping: 30,
                stiffness: 300,
            },
        });
    }, [controls, getHeightPx]);

    const handleDrag = useCallback((
        _event: MouseEvent | TouchEvent | PointerEvent,
        info: PanInfo
    ) => {
        const currentHeight = containerRef.current?.offsetHeight || getHeightPx(SNAP_POINTS.MID);
        const newHeight = currentHeight - info.delta.y;
        const maxHeight = getHeightPx(95);
        const minHeight = getHeightPx(8);

        if (newHeight >= minHeight && newHeight <= maxHeight) {
            controls.set({ height: newHeight });
        }
    }, [controls, getHeightPx]);

    // Expand/collapse on button click
    const handleExpandClick = useCallback(async () => {
        let targetSnap: number;

        if (sheetHeight === SNAP_POINTS.MIN) {
            targetSnap = SNAP_POINTS.MID;
        } else if (sheetHeight === SNAP_POINTS.MID) {
            targetSnap = SNAP_POINTS.MAX;
        } else {
            targetSnap = SNAP_POINTS.MID;
        }

        setSheetHeight(targetSnap);
        await controls.start({
            height: getHeightPx(targetSnap),
            transition: {
                type: "spring",
                damping: 25,
                stiffness: 300,
            },
        });
    }, [sheetHeight, controls, getHeightPx]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            onSearch();
        }
    };

    const isExpanded = sheetHeight === SNAP_POINTS.MAX;

    return (
        <>
            <Overlay
                animate={{
                    opacity: isExpanded ? 1 : 0,
                    pointerEvents: isExpanded ? "auto" : "none",
                }}
                transition={{ duration: 0.2 }}
                onClick={async () => {
                    setSheetHeight(SNAP_POINTS.MID);
                    await controls.start({
                        height: getHeightPx(SNAP_POINTS.MID),
                        transition: { type: "spring", damping: 25, stiffness: 300 },
                    });
                }}
            />
            <BottomSheetContainer
                ref={containerRef}
                animate={controls}
                initial={{ height: getHeightPx(SNAP_POINTS.MID) }}
            >
                <HandleArea>
                    <motion.div
                        drag="y"
                        dragConstraints={{ top: 0, bottom: 0 }}
                        dragElastic={0}
                        onDrag={handleDrag}
                        onDragEnd={handleDragEnd}
                        style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}
                    >
                        <HandleBar />

                        {/* Integrated Search Bar */}
                        <SearchContainer>
                            <SearchTypeButton onClick={onSearchTypeToggle}>
                                {searchType === "title" ?
                                    <IoMdMusicalNote size="1.1rem" /> :
                                    <IoMdPerson size="1.1rem" />
                                }
                            </SearchTypeButton>
                            <SearchInput
                                type="text"
                                value={searchValue}
                                onChange={(e) => onSearchValueChange(e.target.value)}
                                onKeyDown={handleKeyDown}
                                onSubmit={onSearch}
                                placeholder={searchType === "title" ? "제목으로 검색..." : "아티스트로 검색..."}
                            />
                            <IconButton onClick={onSearch}>
                                <IoIosSearch size="1.2rem" />
                            </IconButton>
                            <IconButton onClick={onCalendarToggle}>
                                <IoCalendar size="1rem" />
                            </IconButton>
                        </SearchContainer>

                        <SheetHeader>
                            <HeaderInfo>
                                {totalCount > 0 ? `${songCount}곡 / 총 ${totalCount}곡` : '검색 결과가 여기에 표시됩니다'}
                            </HeaderInfo>
                            <ExpandButton
                                onClick={handleExpandClick}
                                className={isExpanded ? 'expanded' : ''}
                            >
                                {sheetHeight === SNAP_POINTS.MIN ? '펼치기' :
                                 sheetHeight === SNAP_POINTS.MID ? '더 보기' : '접기'}
                                <IoChevronUp size="0.8rem" />
                            </ExpandButton>
                        </SheetHeader>
                    </motion.div>
                </HandleArea>
                <ContentArea>
                    {children}
                </ContentArea>
            </BottomSheetContainer>
        </>
    );
};

export default CustomBottomSheet;
